import React, { FC } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvTable from '../UI/molecules/OvTable';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import EditIcon from '@mui/icons-material/Edit';
import { ArticleDataUtils } from '../../common/utils/services/article-data-utils';
import { useAdminArticle } from '../../hooks/use-admin-article';
import { useAdminArticleMutations } from '../../hooks/use-admin-article-mutations';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { useTranslation } from 'react-i18next';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';

const ARTICLE_LIST_PATH = '/articles';

const ArticleDetails: FC = () => {
  const { articleId } = useParams();
  const { t } = useTranslation();
  const {
    data: article,
    isFetching,
    error: articleError,
    refetch,
  } = useAdminArticle(articleId);
  const { updateArticle } = useAdminArticleMutations();

  const applicationOperations = useAuthorized(Resource.Application);
  const articleOperations = useAuthorized(Resource.Article);
  const articleUpdateAllowed =
    articleOperations.update || applicationOperations.supervise;

  if (!article) {
    return (
      <OvRecordDetailsPage>
        {articleError ? (
          <OvRecordDetailsErrorState
            title={t('articles.details.errorTitle')}
            error={articleError}
            onRetry={() => refetch()}
            backLabel={t('common.articles')}
            backPath={ARTICLE_LIST_PATH}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  const error = articleError ?? updateArticle.error;
  const isLoading = isFetching || updateArticle.isPending;
  const articleDisplayId = article.id as string;

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {error && <OvErrorMessage message={getAxiosErrorMessage(error)} />}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('articles.details.breadcrumb')}
          backControl={
            <OvBackButton
              label={t('common.articles')}
              fallbackPath={ARTICLE_LIST_PATH}
            />
          }
          current={article.title}
          title={article.title}
          meta={
            <>
              <span>
                {articleDisplayId}
                <OvCopyToClipboardButton
                  text={articleDisplayId}
                  label={t('articles.details.copyId')}
                />
              </span>
              {article.topic && <span>{article.topic}</span>}
              {article.updated_at && (
                <span>
                  <OvDateParser
                    date={article.updated_at}
                    format="MMM DD, YYYY - hh:mm A"
                  />
                </span>
              )}
            </>
          }
          actions={
            articleUpdateAllowed && (
              <OvRecordHeaderActions>
                <NavLink to={`/articles/edit/${article.id}`}>
                  <OvCompactButton icon={<EditIcon />}>
                    {t('common.actions.edit')}
                  </OvCompactButton>
                </NavLink>
              </OvRecordHeaderActions>
            )
          }
        />

        <OvRecordDetailsSection title={t('articles.details.section')}>
          <OvTable
            data={ArticleDataUtils.mapDataForArticleDetailsTable(article)}
            theme="Light"
            onSaveRowValue={(updatedProperties) =>
              updateArticle.mutate({ ...article, ...updatedProperties })
            }
          />
        </OvRecordDetailsSection>
      </OvRecordDetailsCanvas>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </OvRecordDetailsPage>
  );
};

export default ArticleDetails;
